import { useState } from 'react';
import { IoTrashOutline } from 'react-icons/io5';

import Error from '../Error';
import Loader from '../Loader';
import Pagination from '../Pagination';
import { tableBackgroundColor } from '../utils/tableBackgroundColor';

import { ThreadError, ThreadMessage } from '@/exceptions/thread/thread.enum';
import useChangeThreadStatus from '@/hooks/thread/useChangeStatus';
import useDeleteThread from '@/hooks/thread/useDeleteThread';
import { useGetAllThreads } from '@/hooks/thread/useGetAllThreads';
import { IThreadResponse } from '@/interfaces/entities/thread';
import { notificationService } from '@/services/notification.service';

const ThreadTable = () => {
	const [loading, setLoading] = useState(false);
	const [currentPage, setCurrentPage] = useState(1);
	const deleteThread = useDeleteThread();
	const changeStatus = useChangeThreadStatus();
	const { threads, isLoading, isError, error, totalPages, isPending } =
		useGetAllThreads(currentPage);

	if (isLoading || isPending) {
		return <Loader />;
	}

	if (isError) {
		return (
			<Error message={error ? error.message : ThreadError.THREADS_ERROR} />
		);
	}

	const handleDelete = (id: number) => {
		notificationService.confirm(ThreadMessage.DELETE_MESSAGE, () => {
			setLoading(true);
			deleteThread.mutate(id);
			setLoading(false);
		});
	};

	const handleChangeStatus = (threadId: number, status: string) => {
		notificationService.confirm(ThreadMessage.CHANGE_STATUS_MESSAGE, () => {
			setLoading(true);
			changeStatus.mutate({ threadId, status });
			setLoading(false);
		});
	};

	return (
		<div className="px-4 sm:px-6 lg:px-8 w-[90%] mb-10">
			<div className="sm:flex sm:items-center">
				<div className="flex justify-between font-bold sm:flex-auto">
					<h1
						data-test="h1-threads"
						className="text-base font-semibold leading-6 text-gray-900"
					>
						Threads
					</h1>
				</div>
			</div>
			<div className="mt-8 flow-root">
				<div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
					<div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
						<table className="min-w-full divide-y divide-gray-300 border-red-300">
							<thead>
								<tr>
									<th
										scope="col"
										className=" px-3 py-3.5 text-center text-sm font-semibold text-gray-900 "
										data-test="th-id"
									>
										ID
									</th>
									<th
										scope="col"
										className=" px-3 py-3.5 text-center text-sm font-semibold text-gray-900"
										data-test="th-title"
									>
										Title
									</th>
									<th
										scope="col"
										className=" px-3 py-3.5 text-center text-sm font-semibold text-gray-900"
										data-test="th-content"
									>
										Content
									</th>
									<th
										scope="col"
										className=" px-3 py-3.5 text-center text-sm font-semibold text-gray-900"
										data-test="th-status"
									>
										Status
									</th>
									<th
										scope="col"
										className=" px-3 py-3.5 text-center text-sm font-semibold text-gray-900"
										data-test="th-category"
									>
										Category
									</th>
									<th
										scope="col"
										className=" px-3 py-3.5 text-center text-sm font-semibold text-gray-900"
										data-test="th-action"
									>
										Action
									</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-gray-200 pr-4">
								{threads.map((thread: IThreadResponse, index) => {
									return (
										<tr
											key={thread.id}
											className={`${tableBackgroundColor(
												index,
											)} hover:bg-gray-200 min-h-8`}
										>
											<td
												data-test="td-id"
												className="text-center max-w-sm overflow-hidden text-ellipsis whitespace-nowrap py-4  text-sm font-medium text-gray-900 truncate"
											>
												{thread.id}
											</td>
											<td
												data-test="td-title"
												className="text-center max-w-md overflow-hidden text-ellipsis whitespace-nowrap px-3 py-4 text-sm text-gray-500  truncate"
											>
												{thread.title}
											</td>
											<td
												data-test="td-content"
												className="text-center max-w-md overflow-hidden text-ellipsis whitespace-nowrap px-3 py-4 text-sm text-gray-500  truncate"
											>
												{thread.content}
											</td>
											<td
												data-test="td-status"
												className="relative whitespace-nowrap text-center py-4  text-sm font-medium sm:pr-0"
											>
												<button
													data-test="edit-thread-button"
													className="text-indigo-600 hover:text-indigo-900 disabled:text-gray-300"
													disabled={loading}
													onClick={() =>
														handleChangeStatus(
															thread.id,
															thread.status === 'PENDING' ||
																thread.status === 'REJECTED'
																? 'ACCEPTED'
																: 'REJECTED',
														)
													}
												>
													{thread.status === 'PENDING' ||
													thread.status === 'REJECTED'
														? 'Accept'
														: 'Reject'}
													<span className="sr-only">{thread.id}</span>
												</button>
											</td>
											<td className="whitespace-nowrap text-center py-4 pl-3 pr-4 text-sm font-medium sm:pr-0">
												{thread?.category?.name}
											</td>
											<td
												data-test="delete-thread-button"
												className="flex justify-center  py-4  text-sm font-medium"
											>
												<IoTrashOutline
													className="cursor-pointer"
													onClick={() => handleDelete(thread.id)}
													aria-disabled={loading}
												/>
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
						<Pagination
							currentPage={currentPage}
							setCurrentPage={setCurrentPage}
							totalPages={totalPages as number}
						/>
					</div>
				</div>
			</div>
		</div>
	);
};

export default ThreadTable;
